import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react'

import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'

import { Label } from '../atoms'

import SelectProject from '../SelectProject/SelectProject'
import SelectAccount from '../SelectAccount/SelectAccount'
import SelectRecipe from '../SelectRecipe/SelectRecipe'

import { CreateStudyProps } from './CreateStudy.types'

import { Project, ProjectRecipeFilter, RecipeInterface } from '@pollination-solutions/pollination-sdk'

import { ProjectJobInfo, useCreateStudy } from '../hooks/useCreateStudy'
import { LocalConfig, useRuns } from '../hooks'
import 'react-tabs/style/react-tabs.css'
import { Account } from '../SelectAccount/SelectAccount.types'

import toast, { Toaster } from 'react-hot-toast'

import RecipeForm from '../RecipeForm/RecipeForm'

const defaultStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
}

export const CreateStudy: React.FC<CreateStudyProps> = ({
  style,
  defaultAccount,
  defaultProject,
  defaultRecipe,
  authUser,
  client,
  onSuccess = undefined,
}) => {

  const [activeTabIndex, setActiveTabIndex] = useState(0)

  const [selAccount, setSelAccount] = useState<Account>(defaultAccount)
  const [selProject, setSelProject] = useState<Project>(defaultProject)
  const [selRecipe, setSelRecipe] = useState<RecipeInterface>()
  const [selRecipeFilter, setSelRecipeFilter] = useState<ProjectRecipeFilter>(defaultRecipe)

  useEffect(() => {
    if(!selRecipeFilter) {
      setSelRecipe(undefined)
      return
    }

    // Remove tag from name
    // const clearName = selRecipeFilter
    //   .name.replace(selRecipeFilter.tag, '')
    //   .slice(0, -1)
    
    // If * use latest
    const conditionalTag = selRecipeFilter.tag === '*'
      ? 'latest'
      : selRecipeFilter.tag

    client.recipes.getRecipeByTag({ 
      owner: selRecipeFilter.owner, 
      name: selRecipeFilter.name, 
      tag: conditionalTag 
    })
    .then(d => {
      setSelRecipe(d.data.manifest)
    }).catch(err => {
      toast.error('Recipe not found. Try a new tag!',
        { duration: 2000, position: 'top-center', style: { minWidth: '300px', fontSize: '14px' } 
      })
      setSelRecipe(undefined)
    })

  }, [selRecipeFilter])

  // @ts-ignore
  const projectOwner: string | undefined = selAccount ? (selAccount.username ?? selAccount.account_name) : undefined
  const projectSlug: string | undefined = selProject ? selProject.slug.split('/')[1] : undefined
  
  const { host, createStudy, createLocalStudy } = useCreateStudy(projectOwner, projectSlug, client)

  useEffect(() => {
    if (!selRecipe) return
    setActiveTabIndex(1)
  }, [selRecipe])

  const basePath = useMemo(() => {
    return client.config.basePath.replace('api', 'app')
  }, [client])

  const _onSuccessCloud = (projectJobInfo: ProjectJobInfo) => {
    if (!basePath) {
      throw new Error('CreateStudy: client.configuration.basepath not defined.')
    }

    // There are errors
    if (!projectJobInfo.studyId) return

    // Custom or default callback
    if (onSuccess) {
      onSuccess(projectJobInfo, true)
    } else {
      toast.success('Study submitted!',
        { duration: 4000, position: 'bottom-left', style: { minWidth: '300px', fontSize: '14px' } 
      })

      setTimeout(() => { 
        window.location.href = `${basePath}/${projectJobInfo.accountName}/projects/${projectJobInfo.projectName}/studies/${projectJobInfo.studyId}?tab=details&perPage=5&status=null&page=1`
      }, 3000)
    }
  }

  const toUnixPath = path => path.replace(/[\\/]+/g, '/').replace(/^([a-zA-Z]+:|\.\/)/, '')

  const _onSuccessLocal = (projectJobInfo: ProjectJobInfo) => {
    if (!basePath) {
      throw new Error('CreateStudy: client.configuration.basepath not defined.')
    }
    
    // There are errors
    if (!projectJobInfo.studyId) return

    // Custom or default callback
    // local study ID === run ID
    if (onSuccess) {
      onSuccess(projectJobInfo, false)
    } else {
      toast.success(`Study is running! Do not close the console.\nYou will find output here ${projectJobInfo.studyId}.`,
        { duration: 4000, position: 'bottom-left', style: { minWidth: '300px', fontSize: '12px' } })

      // Formatted name of the job
      const jobName = toUnixPath(projectJobInfo.studyId).split('/').reverse()[0]

      // Path is the full path of the local machine where the run input/output are
      setTimeout(() => { 
        window.location.href = `${basePath}/${projectJobInfo.accountName}/projects/${projectJobInfo.projectName}/localStudies/${jobName}/?path=${projectJobInfo.studyId}&studyName=${projectJobInfo.jobName}`
      }, 3000)
    }
  }

  const handleCreateStudy = useCallback((name: string, jobArgs: any[], 
    description?: string, localConfig?: LocalConfig, 
    userRecipe?: RecipeInterface) => {
    if (!selRecipe) {
      return Promise.reject()
    }

    if (host !== 'web' && localConfig) {
      return createLocalStudy(
        {
          name,
          recipe: selRecipe,
          description: description ?? 'Study created from pollination',
          recipeFilter: selRecipeFilter,
          authUserName: authUser.username
        },
        jobArgs,
        localConfig,
        localConfig.isLocalJob ? _onSuccessLocal : _onSuccessCloud,
        userRecipe
      )
    } else {
      return createStudy(
        {
          name,
          recipe: selRecipe,
          description: description ?? 'Study created from pollination',
        },
        jobArgs,
        _onSuccessCloud,
      )
    }

  }, [selRecipe, host, createLocalStudy, onSuccess, createStudy])

  return (
    <div data-testid="CreateProject" style={{ ...defaultStyle, ...style }}>
      <Tabs 
        selectedIndex={activeTabIndex} 
        onSelect={(index) => setActiveTabIndex(index)}
        forceRenderTabPanel={true}
      >
        <div
          style={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <TabList
            style={{
              flex: 1
            }}
          >
            <Tab>Select Recipe</Tab>
            {selRecipe &&
              <Tab>Recipe Inputs</Tab>
            }
          </TabList>
        </div>
        <TabPanel
          style={{
            padding: '8px 8px'
          }}
        >
          <div
            style={{
              display:'flex',
              flexDirection:'column',
              gap:8
            }}
          >
            <Label
              label={selAccount ? selAccount.name : 'Account'}
            >
              <SelectAccount
                authUser={authUser}
                client={client}
                onChange={setSelAccount}
                value={selAccount}
              />
            </Label>
            <Label
              label={selProject ? selProject.name : 'Project'}
            >
              <SelectProject
                authUser={authUser}
                client={client}
                onChange={setSelProject}
                projectOwner={projectOwner}
                value={selProject}
              />
            </Label>
            <Label
              label={selRecipe && selRecipe.metadata ? 
                selRecipe.metadata.name : 'Recipe'}
            >
              <SelectRecipe
                authUser={authUser}
                client={client}
                projectOwner={projectOwner}
                projectName={selProject ? selProject.name : undefined}
                onChange={setSelRecipeFilter}
                value={selRecipeFilter}
              />
              <Toaster />
            </Label>
          </div>
        </TabPanel>
        {selRecipe &&
          <>
            <TabPanel
              style={{
                padding: 8
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 24
                }}
              >
                <RecipeForm 
                  client={client}
                  projectOwner={projectOwner}
                  projectName={projectSlug}
                  recipe={selRecipe} 
                  onSubmit={handleCreateStudy} 
                  style={style}
                />
                <Toaster />
              </div>
            </TabPanel>
          </>
        }
      </Tabs>
    </div>
  )
}

export default CreateStudy